import React, { useRef, useEffect, useState } from "react";
import Slider from "react-slick";
import { X } from "lucide-react";
import { FaPlayCircle } from "react-icons/fa";
import rightuparrow from "../../assets/right-up-arrow.png";
import bgImage from "../../assets/KeyFeatures_BackgroundColor.png";
import video2 from "../../assets/video-2.png";
import FancyGetStartedButton from "./FancyGetStartedButton";
import GlowButton from "./GlowButton";
import "../../styles/WatchDemoVideos.css";

const demoSlides = [
  {
    id: 1,
    title: "ai ordering support",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_AI_ORDERING,
    videoType: "video/mp4",
  },
  {
    id: 2,
    title: "geo digital presence",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_GEO_PRESENCE,
    videoType: "video/mp4",
  },
  {
    id: 3,
    title: "in chat ordering",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_IN_CHAT_ORDERING,
    videoType: "video/mp4",
  },
  {
    id: 4,
    title: "up front payments",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_UPFRONT_PAYMENTS,
    videoType: "video/mp4",
  },
  {
    id: 5,
    title: "payments and wallet",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_PAYMENTS_WALLET || null,
    videoType: "video/mp4",
  },
  {
    id: 6,
    title: "order queue management",
    desc: "get a closer look at how beɘmz transforms operations and customer experience. watch our demo video to see real time ordering, ai support and seamless service in action — built to help your business grow faster and smarter.",
    videoUrl: process.env.REACT_APP_DEMO_VIDEO_ORDER_QUEUE,
    videoType: "video/mp4",
  },
];

const WatchOurDemo = () => {
  const [playingIndex, setPlayingIndex] = useState(null);
  const [current, setCurrent] = useState(0); // for custom dots
  const sliderRef = useRef(null);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const settings = {
    dots: false,
    infinite: true,
    speed: 80,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 7000,
    pauseOnHover: true,
    arrows: false,
    swipe: true,
    adaptiveHeight: true,
    afterChange: (i) => setCurrent(i),
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!sliderRef.current) return;
      if (e.key === "ArrowRight") sliderRef.current.slickNext();
      if (e.key === "ArrowLeft") sliderRef.current.slickPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <div className="w-full flex items-center justify-center bg-white py-6 md:py-28 px-4 sm:px-8 md:px-[43px] lg-xl:px-[43px] xl-2xl:px-[46px]">
      <section
        className="relative w-full max-w-[1600px] xl-2xl:h-[690px] flex items-center justify-center
        bg-cover bg-center bg-no-repeat text-white rounded-3xl overflow-visible"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="watch-demo-slider max-w-[1340px] w-full mx-auto relative pb-2">
          <Slider ref={sliderRef} {...settings}>
            {demoSlides.map((slide, index) => (
              <div key={slide.id} className="px-2 md:px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                  {/* Left (TEXT) – second on mobile, first on desktop */}
                  <div
                    className="
                      order-2 md:order-1
                      space-y-6 w-160
                      mt-[-1.5rem] sm:mt-[-1rem] md:mt-0   /* ⬅️ text up only on mobile */
                      text-left md:ml-[1px] lg-xl:ml-[1px] xl-2xl:-ml-[25px] 2xl-3xl:ml-[20px]
                      text-center md:text-left xl-2xl:w-[600px]
                    "
                  >
                    <h2 className="text-4xl sm:text-5xl md:text-5xl font-bold leading-snug sm:pt-[25px]">
                      <span>
                        <div className="py-2 sm:py-0 md:py-0 sm:text-[30px] md:text-[35px] xl-2xl:text-[55px]">
                          watch our demo
                        </div>
                        <div className="text-[#FCBA05] py-2 sm:py-0 sm:text-[30px] md:text-[35px] xl-2xl:text-[55px] leading-[40px]">
                          videos to see beɘmz
                        </div>
                        <div className="py-2 sm:py-0 md:py-0 sm:text-[30px] md:text-[35px] xl-2xl:text-[55px]">
                          in action
                        </div>
                      </span>
                      <div className="mt-2 text-[18px] sm:text-[20px] md:text-[24px] xl-2xl:text-[28px] font-semibold tracking-wide">
                        — {slide.title}
                      </div>
                    </h2>

                    <p
                      className="text-white text-base md:text-[20px] tracking-wide leading-[26px] tracking-[0.08em]"
                      dangerouslySetInnerHTML={{ __html: slide.desc }}
                    />

                    <div className="flex flex-wrap justify-center md:justify-start items-center gap-4">
                      <button
                        onClick={() => window.location.href = "/business/demo"}
                        className="h-[48px] inline-flex items-center justify-center whitespace-nowrap border border-[#6E3FC6] bg-[#e9d9ff] hover:bg-[#d8bfff] text-black text-sm sm:text-base font-medium px-[18px] sm:px-[25px] lg:px-[40px] rounded-full transition-all duration-300 cursor-pointer"
                      >
                        how it works
                      </button>
                      <span><GlowButton btnValue="free trial" btn1="2" onMouseOver={() => setIsMouseOver(true)}
                        onMouseOut={() => setIsMouseOver(false)}
                        isMouseOver={isMouseOver} onClick={() => window.location.href = "/business/get-started"} /></span>
                    </div>
                  </div>

                  {/* Right (VIDEO / IMAGE) – first on mobile, second on desktop */}
                  <div className="order-1 md:order-2 relative flex flex-col items-center py-4 md:py-[50px]">
                    <div className="relative w-full h-[220px] sm:h-[270px] md:h-[520px] xl-2xl:h-[450px] 2xl-3xl:h-[410px] sm:w-[300px] xl-2xl:w-[600px] 2xl-3xl:w-[470px] xl-2xl:ml-[30px] rounded-2xl overflow-hidden shadow-2xl bg-black">
                      {playingIndex === index ? (
                        <div className="relative w-full h-full">
                          {slide.videoUrl ? (
                            <video
                              className="w-full h-full rounded-2xl object-contain bg-black"
                              src={slide.videoUrl}
                              autoPlay
                              controls
                              playsInline
                            >
                              <source src={slide.videoUrl} type={slide.videoType} />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-black/80 rounded-2xl">
                              <p className="text-white text-center text-sm px-4">Video coming soon</p>
                            </div>
                          )}
                          <button
                            onClick={() => setPlayingIndex(null)}
                            className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-all"
                            aria-label="Close video"
                          >
                            <X className="w-6 h-6" />
                          </button>
                        </div>
                      ) : (
                        <>
                          {slide.videoUrl ? (
                            <video
                              className="w-full h-full object-contain bg-black rounded-2xl"
                              src={`${slide.videoUrl}#t=0.001`}
                              preload="metadata"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={video2}
                              alt={slide.title}
                              className="w-full h-full object-cover rounded-2xl"
                            />
                          )}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <button onClick={() => setPlayingIndex(index)}>
                              <FaPlayCircle className="text-white/90 text-[90px] drop-shadow-lg" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Mobile-only dots directly under image */}
                    <div className="mt-4 flex items-center gap-3 md:hidden">
                      {demoSlides.map((_, i) => {
                        const isActive = i === current;
                        return (
                          <button
                            key={i}
                            onClick={() => sliderRef.current?.slickGoTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                            className={`transition-all duration-300 ${isActive
                                ? "w-8 h-1 rounded-full bg-white"
                                : "w-2 h-2 rounded-full bg-white/70"
                              }`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Desktop / laptop dots at bottom (unchanged) */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-50 hidden md:flex items-center gap-3">
          {demoSlides.map((_, i) => {
            const isActive = i === current;
            return (
              <button
                key={i}
                onClick={() => sliderRef.current?.slickGoTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`transition-all duration-300 ${isActive
                    ? "w-8 h-1 rounded-full bg-white"
                    : "w-2 h-2 rounded-full bg-white/70"
                  }`}
              />
            );
          })}
        </div>

        {/* Decorative overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.06)_0%,_transparent_60%)] pointer-events-none" />
      </section>
    </div>
  );
};

export default WatchOurDemo;
